import { Link } from 'react-router-dom';
import { ArrowLeft, Shield, Database, Lock, Users, FileText, Globe, Mail, MapPin, AlertCircle, Scale } from 'lucide-react';
import { ROUTES } from '@constants';
import Button from '@components/common/Button';
import Card from '@components/common/Card';

const PrivacyPolicy = () => {
  const sections = [
    {
      id: 'introduction',
      title: 'Introduction',
      icon: FileText,
      content: (
        <>
          <p className="mb-4">
            This Privacy Policy ("Policy") is issued by <strong>Flatoutway Consultancy Group</strong> ("Company", "we", "our", "us"),
            having its registered office at:
          </p>
          <div className="bg-primary-50 rounded-lg p-4 mb-4">
            <p className="text-sm text-primary-900 font-semibold">
              1st Floor, B-23 618/1, Balmik Mandir Wali Gali, Hoshiarpur, Punjab – 146001, India
            </p>
          </div>
          <p className="mb-4">
            This Policy governs the collection, use, storage, and disclosure of personal information of users ("User", "you")
            accessing our website, web application, and educational services ("Platform").
          </p>
          <div className="bg-yellow-50 border-l-4 border-yellow-400 p-4 rounded-r-lg">
            <p className="text-sm text-yellow-800 font-semibold">
              By using the Platform, you consent to this Policy.
            </p>
          </div>
        </>
      ),
    },
    {
      id: 'information-collected',
      title: 'Information Collected',
      icon: Database,
      content: (
        <>
          <p className="mb-4">We may collect the following information:</p>
          <ul className="list-disc list-inside space-y-2 ml-4">
            <li>Personal details (name, email, mobile number, address)</li>
            <li>Academic and course-related information</li>
            <li>Payment and billing details (excluding card data)</li>
            <li>Technical data (IP address, device, browser, usage data)</li>
          </ul>
        </>
      ),
    },
    {
      id: 'purpose-of-collection',
      title: 'Purpose of Collection',
      icon: AlertCircle,
      content: (
        <>
          <p className="mb-4">Your information is collected for:</p>
          <ul className="list-disc list-inside space-y-2 ml-4">
            <li>Providing educational and consultancy services</li>
            <li>Processing registrations and payments</li>
            <li>Communication and support</li>
            <li>Improving Platform performance and user experience</li>
            <li>Compliance with applicable laws</li>
          </ul>
        </>
      ),
    },
    {
      id: 'data-sharing',
      title: 'Data Sharing',
      icon: Users,
      content: (
        <>
          <p className="mb-4">We may share information:</p>
          <ul className="list-disc list-inside space-y-2 ml-4 mb-4">
            <li>With service providers (payment gateways, IT support)</li>
            <li>With academic or business partners (where applicable)</li>
            <li>When required under applicable law or by government authorities</li>
          </ul>
          <div className="bg-green-50 border-l-4 border-green-400 p-4 rounded-r-lg">
            <p className="text-sm text-green-800 font-semibold">
              We do not sell personal data.
            </p>
          </div>
        </>
      ),
    },
    {
      id: 'data-security',
      title: 'Data Security',
      icon: Lock,
      content: (
        <p>
          We implement reasonable security practices in accordance with applicable Indian laws, including the
          Information Technology Act, 2000, to protect user data.
        </p>
      ),
    },
    {
      id: 'data-retention',
      title: 'Data Retention',
      icon: Database,
      content: (
        <p>
          Personal data shall be retained only as long as necessary for service delivery and legal compliance.
        </p>
      ),
    },
    {
      id: 'user-rights',
      title: 'User Rights',
      icon: Shield,
      content: (
        <p>
          Users may request access, correction, or deletion of their personal data by contacting us.
        </p>
      ),
    },
    {
      id: 'third-party-links',
      title: 'Third-Party Links',
      icon: Globe,
      content: (
        <p>
          The Platform may contain links to third-party websites. The Company is not responsible for their privacy
          practices.
        </p>
      ),
    },
    {
      id: 'policy-updates',
      title: 'Policy Updates',
      icon: AlertCircle,
      content: (
        <p>
          We reserve the right to update this Policy. Changes will be posted on the Platform with the revised effective
          date.
        </p>
      ),
    },
    {
      id: 'governing-law',
      title: 'Governing Law',
      icon: Scale,
      content: (
        <p>
          This Policy shall be governed by the laws of India. Any disputes shall be subject to the jurisdiction of courts in
          Punjab.
        </p>
      ),
    },
    {
      id: 'contact-details',
      title: 'Contact Details',
      icon: Mail,
      content: (
        <>
          <p className="mb-4">
            For any questions or concerns regarding this Privacy Policy, please contact us at:
          </p>
          <div className="bg-primary-50 rounded-lg p-6 space-y-3">
            <div className="flex items-start gap-3">
              <MapPin className="w-5 h-5 text-primary-600 flex-shrink-0 mt-0.5" />
              <div>
                <p className="font-semibold text-primary-900">Flatoutway Consultancy Group</p>
                <p className="text-sm text-primary-700">B-23 618/1 1st Floor, Balmik mandir wali gali, Ravidass Nagar</p>
                <p className="text-sm text-primary-700">Hoshiarpur, 146001, Punjab, INDIA</p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <Mail className="w-5 h-5 text-primary-600 flex-shrink-0" />
              <a href="mailto:flatoutway@gmail.com" className="text-sm text-primary-700 hover:text-primary-900 transition-colors">
                flatoutway@gmail.com
              </a>
            </div>
            <div className="pt-3 border-t border-primary-200">
              <p className="text-sm text-primary-600">
                <strong>Effective Date:</strong> 30 March 2026
              </p>
            </div>
          </div>
        </>
      ),
    },
  ];

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Hero Section */}
      <section className="bg-gradient-to-br from-primary-50 via-secondary-50 to-primary-100 py-12 md:py-16">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-4xl mx-auto text-center">
            <div className="inline-block mb-4 px-4 py-2 bg-secondary-100 rounded-full">
              <span className="text-sm font-semibold text-secondary-700 flex items-center gap-2">
                <Shield className="w-4 h-4" />
                Privacy & Data Protection
              </span>
            </div>
            
            <h1 className="text-3xl sm:text-4xl md:text-5xl font-bold text-primary-900 mb-4">
              Privacy Policy
            </h1>
            
            <p className="text-base sm:text-lg text-primary-600 mb-6">
              Effective Date: 30 March 2026
            </p>
            
            <p className="text-base text-primary-700 max-w-2xl mx-auto">
              We are committed to protecting your privacy and ensuring the security of your personal information. 
              Please read this policy carefully to understand how we collect, use, and safeguard your data.
            </p>
            
            <div className="mt-8">
              <Link to={ROUTES.HOME}>
                <Button variant="outline" size="md" className="inline-flex items-center gap-2">
                  <ArrowLeft className="w-4 h-4" />
                  Back to Home
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Table of Contents */}
      <section className="py-8 bg-white border-b border-gray-200">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-xl font-bold text-primary-900 mb-4">Table of Contents</h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
              {sections.map((section, index) => (
                <a
                  key={section.id}
                  href={`#${section.id}`}
                  className="text-sm text-primary-600 hover:text-primary-900 hover:underline transition-colors"
                >
                  {index + 1}. {section.title}
                </a>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Content Sections */}
      <section className="py-12 bg-gray-50">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-4xl mx-auto space-y-8">
            {sections.map((section, index) => {
              const Icon = section.icon;
              return (
                <Card key={section.id} id={section.id} className="p-6 md:p-8 scroll-mt-24">
                  <div className="flex items-start gap-4 mb-4">
                    <div className="flex-shrink-0 w-12 h-12 bg-gradient-to-br from-primary-500 to-secondary-500 rounded-lg flex items-center justify-center">
                      <Icon className="w-6 h-6 text-white" />
                    </div>
                    <div className="flex-1">
                      <div className="text-sm font-semibold text-secondary-600 mb-1">
                        Section {index + 1}
                      </div>
                      <h2 className="text-2xl font-bold text-primary-900">
                        {section.title}
                      </h2>
                    </div>
                  </div>
                  <div className="text-primary-700 leading-relaxed">
                    {section.content}
                  </div>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* Footer CTA */}
      <section className="py-12 bg-white border-t border-gray-200">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-4xl mx-auto text-center">
            <h3 className="text-2xl font-bold text-primary-900 mb-4">
              Questions About Our Privacy Policy?
            </h3>
            <p className="text-base text-primary-600 mb-6">
              If you have any questions or concerns about our privacy practices, please don't hesitate to contact us.
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
              <a href="mailto:flatoutway@gmail.com">
                <Button variant="primary" size="lg" className="inline-flex items-center gap-2">
                  <Mail className="w-4 h-4" />
                  Contact Us
                </Button>
              </a>
              <Link to={ROUTES.HOME}>
                <Button variant="outline" size="lg" className="inline-flex items-center gap-2">
                  <ArrowLeft className="w-4 h-4" />
                  Back to Home
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default PrivacyPolicy;
